// @flow
// $FlowFixMe
import React, { memo, useEffect, useState, useContext } from "react";
import { easings, useSpring } from "@react-spring/web";
import { buildPromoBadgeURL, isFullURL } from "@tvg/formatter/url";
import type { NullaryFn, UnaryFn, BinaryFn } from "@tvg/types/Functional";
import type { RunnerHandicapping } from "@fdr/types/ProgramPage";
import { DerbyBanner } from "@urp/derby-banner";
import { BannerProps } from "@urp/derby-banner/src/types";
import type {
  RaceProgram,
  RaceListItem,
  RaceVideoFeedback,
  RaceCardBettingInterest
} from "@tvg/types/Race";
import { noop, get } from "lodash";
import Icon from "@fdr/static-ui/Icons";
import { arrowLeft, arrowDown } from "@fdr/static-ui/Icons/icons";
import { getTrackDescription } from "@fdr/utils/raceUtils";
import { FeedbackMessageContext } from "@fdr/utils/contextUtils";
import buildColor from "@fdr/static-ui/ColorPalette";
import Flag from "@fdr/static-ui/Flags";
import { useWindowScrollPosition } from "@fdr/custom-hooks/ProgramPage";
import MTP, { DefaultMTPText } from "../../_atoms/MTP";
import TrackSelector from "../../_molecules/TrackSelector";
import VideoButton from "../../_molecules/VideoButton";
import {
  Container,
  HorizontalContainer,
  RaceSelectorWrapper,
  LinkContainer,
  Navigation,
  Title,
  WrapperTitle,
  CountryTitle,
  NavigationWrapper,
  SubTitle,
  TrackDescription,
  TitleContainer,
  ButtonContainer,
  TrackNameContainer,
  RaceNumberContainer,
  IconWrapper,
  FloatButtonContainer,
  RaceTitleHeader
} from "./styled-components";
import Text from "../../_atoms/Text";
import TrackSelectorButton from "../../_atoms/TrackSelectorButton";
import PromoButton, {
  type PromoText,
  PromoTextXSell
} from "../../_molecules/PromoButton";
import type { MTPTextTime } from "../../_atoms/MTP";
import Button from "../../_atoms/Button/index";

type Props = {
  isHeaderSelector: boolean,
  race: RaceProgram,
  derbyRace?: RaceProgram,
  raceInfoData: ?(RaceListItem[]),
  onEventClick: UnaryFn<string, void>,
  onBackPageLink: string,
  onForwardPageLink: string,
  isBackDisabled: boolean,
  isForwardDisabled: boolean,
  hasPromo: boolean,
  trackSelectorUrl: string,
  prevPath: string,
  showRace: boolean,
  videoFeedBack: RaceVideoFeedback,
  onVideoClick: NullaryFn<void>,
  bettingInterests: RaceCardBettingInterest[][],
  loadingRunners: boolean,
  raceAnalystVerdictExpanded: boolean,
  setRaceAnalystVerdictExpanded: UnaryFn<boolean, void>,
  isWagerable: boolean,
  isSRW: boolean,
  freePickSource: string,
  racePromoPathParsed: ?string,
  MTPText: MTPTextTime,
  promoText: PromoText,
  withFixedBanner?: boolean,
  isNightMode?: boolean,
  hasDerbyBanner?: boolean,
  hasFAQ?: boolean,
  eventStatus: string,
  bannerCfg?: BannerProps,
  onBannerCtaClick: BinaryFn<string, string, void>
};

const forbiddenPreviousPages = ["racetracks"];

const getGoBackExtraProps = (prevPath: string = "") => ({
  to:
    prevPath &&
    !forbiddenPreviousPages.find((page) => new RegExp(page, "g").test(prevPath))
      ? prevPath
      : "/home",
  isTrackTitle: true
});
const renderHeaderSelector = (props: Props) => {
  const { race, onEventClick, prevPath, showRace, trackSelectorUrl } = props;
  return (
    <RaceSelectorWrapper isNavigationShown={showRace}>
      <LinkContainer
        onClick={() => onEventClick("GO_BACK")}
        {...getGoBackExtraProps(prevPath)}
      >
        <Icon icon={arrowLeft} size={24} color="white" />
        <Text bold fontSize={16} color="white">
          Back
        </Text>
      </LinkContainer>

      <Container isHeaderSelector>
        <MTP
          mtp={race.mtp}
          status={get(race, "status.code")}
          postTime={race.postTime}
          isDarkBackground
          isNewLayout
          isSmall
          inline
          isSmallText
        />
        <TrackSelector
          isHeaderSelector
          trackName={get(race, "track.trackName")}
          raceNumber={race.raceNumber}
          openTrackSelector={onEventClick}
          trackSelectorUrl={trackSelectorUrl}
        />
      </Container>
    </RaceSelectorWrapper>
  );
};

export const getRunnersHandicapping = (
  bettingInterests: RaceCardBettingInterest[][],
  freePickSource: string
): RunnerHandicapping[] => {
  const handicappingArray = [];

  get(bettingInterests, "[0]", []).forEach((betInterest) => {
    betInterest.runners &&
      betInterest.runners.forEach(
        (runner) =>
          get(runner, `${freePickSource}.freePick.number`, false) &&
          handicappingArray.push({
            runnerId: runner.runnerId,
            freePickNumber: get(runner, `${freePickSource}.freePick.number`),
            freePickInfo: get(runner, `${freePickSource}.freePick.info`)
          })
      );
  });

  return handicappingArray.sort((bi1, bi2) =>
    bi1.freePickNumber > bi2.freePickNumber ? 1 : -1
  );
};

const renderStandardSelector = (props: Props) => {
  const {
    race,
    derbyRace,
    onEventClick,
    onBackPageLink,
    onForwardPageLink,
    isBackDisabled,
    isForwardDisabled,
    trackSelectorUrl,
    videoFeedBack,
    onVideoClick,
    racePromoPathParsed,
    MTPText,
    prevPath,
    loadingRunners,
    withFixedBanner,
    isNightMode,
    hasDerbyBanner,
    hasFAQ,
    eventStatus,
    bannerCfg,
    onBannerCtaClick
  } = props;

  const maxScrollPosition = hasDerbyBanner ? 170 : 140;
  const scrollPosition = useWindowScrollPosition(100);
  const [firstLoad, setFirstLoad] = useState(true);
  const [collapsedHeader, setCollapsedHeader] = useState(false);
  const config = { duration: 300, easing: easings.easeInOutCirc };
  const raceTitleHeaderConfig = () => ({
    opacity: collapsedHeader ? 0 : 1,
    y: collapsedHeader ? -7 : 0,
    scale: collapsedHeader ? 0.98 : 1,
    config
  });
  const raceSelectionHeaderConfig = () => ({
    opacity: collapsedHeader ? 1 : 0,
    y: collapsedHeader ? 0 : 7,
    scale: collapsedHeader ? 1 : 0.98,
    config
  });
  const [raceTitleHeaderStyles, raceTitleHeaderApi] = useSpring(
    raceTitleHeaderConfig
  );
  const [raceSelectionHeaderStyles, raceSelectionHeaderApi] = useSpring(
    raceSelectionHeaderConfig
  );

  useEffect(() => {
    if (loadingRunners) return;

    if (firstLoad) {
      setFirstLoad(false);
      setCollapsedHeader(false);
    } else {
      setCollapsedHeader(scrollPosition > maxScrollPosition);
    }
  }, [scrollPosition, loadingRunners]);

  useEffect(() => {
    raceTitleHeaderApi.start(raceTitleHeaderConfig());
    raceSelectionHeaderApi.start(raceSelectionHeaderConfig());
  }, [collapsedHeader]);

  const isOptedIn = get(race, "promo.isOptedIn");
  const promoPath = racePromoPathParsed || get(race, "promo.promoPath", "");
  const isExternal = isFullURL(promoPath);
  const { trackTitle, trackSubtitle, trackCity } = getTrackDescription(
    get(race, "track.trackName", ""),
    get(race, "track.trackLocation", {}),
    get(race, "highlighted")
  );
  const trackLocation = get(race, "track.trackLocation", {});
  const displayVideoContainer =
    !(
      videoFeedBack.indexOf("AVAILABLE") !== 0 &&
      videoFeedBack.includes("NO_REPLAY")
    ) ||
    videoFeedBack.includes("STARTING_SOON_NO_REPLAY") ||
    get(race, "promo");

  const postTimeAvailableRace = get(derbyRace, "postTime");

  return (
    <React.Fragment>
      <TitleContainer
        isNightMode={isNightMode}
        withFixedBanner={withFixedBanner}
        hasDerbyBanner={hasDerbyBanner}
      >
        <WrapperTitle>
          <LinkContainer
            onClick={() => onEventClick("GO_BACK")}
            {...getGoBackExtraProps(prevPath)}
          >
            <Icon
              icon={arrowLeft}
              size={28}
              color={
                isNightMode ? buildColor("white") : buildColor("grey", "D4")
              }
            />
          </LinkContainer>

          <RaceTitleHeader
            style={raceTitleHeaderStyles}
            show={!collapsedHeader}
          >
            <TrackDescription data-qa-label="track-description">
              <Flag country={trackLocation.country} circular />
              <Title isNightMode={isNightMode}>{trackTitle}</Title>
              <CountryTitle isNightMode={isNightMode}>{trackCity}</CountryTitle>
            </TrackDescription>
            {!hasDerbyBanner && trackSubtitle && (
              <SubTitle>{trackSubtitle}</SubTitle>
            )}
            {hasDerbyBanner && hasFAQ && (
              <FloatButtonContainer>
                <Button
                  type="tertiary"
                  size="medium"
                  text="FAQ"
                  isInternalLink
                  url="/tutorials#FAQ"
                  isNightMode
                />
              </FloatButtonContainer>
            )}
          </RaceTitleHeader>
          <RaceTitleHeader
            style={raceSelectionHeaderStyles}
            show={collapsedHeader}
          >
            <ButtonContainer>
              <Button
                type="link"
                size="noSize"
                onClick={() => onEventClick("OPEN_TRACK_MODAL")}
                isInternalLink
                url={trackSelectorUrl}
                borderLess
                paddingLess
              >
                <TrackNameContainer isNightMode={isNightMode}>
                  {trackTitle}
                </TrackNameContainer>
                <RaceNumberContainer isNightMode={isNightMode}>
                  &nbsp;R{race.raceNumber}
                </RaceNumberContainer>
                <IconWrapper
                  icon={arrowDown}
                  color={
                    isNightMode ? buildColor("white") : buildColor("blue", "D1")
                  }
                />
              </Button>
              <MTP
                size={14}
                mtp={race.mtp}
                status={get(race, "status.code")}
                postTime={race.postTime}
                mode={isNightMode ? "derby" : "light"}
                textTime={MTPText}
                isNewLayout
                isSmall
                showDescription
                inline
                condensedDescription
              />
            </ButtonContainer>
            <FloatButtonContainer>
              <VideoButton
                feedback={videoFeedBack}
                small
                onClick={onVideoClick}
                isNightMode={isNightMode}
              />
            </FloatButtonContainer>
          </RaceTitleHeader>
        </WrapperTitle>
      </TitleContainer>
      <DerbyBanner
        raceStartDate={
          postTimeAvailableRace ? new Date(postTimeAvailableRace) : null
        }
        eventStatus={eventStatus}
        bannerCfg={bannerCfg}
        onCtaClick={onBannerCtaClick}
      />
      <Container>
        <HorizontalContainer isNightMode={isNightMode}>
          <MTP
            size={14}
            mtp={race.mtp}
            status={get(race, "status.code")}
            isDarkBackground
            mode={isNightMode ? "derby" : "default"}
            postTime={race.postTime}
            isNewLayout
            textTime={MTPText}
          />
          <NavigationWrapper>
            <TrackSelectorButton
              raceNumber={race.raceNumber}
              openTrackSelector={() => onEventClick("OPEN_TRACK_MODAL")}
              trackSelectorUrl={trackSelectorUrl}
              isNightMode={isNightMode}
            />
            <Navigation
              onEventClick={onEventClick}
              onBackPageLink={onBackPageLink}
              onForwardPageLink={onForwardPageLink}
              isBackDisabled={isBackDisabled}
              isForwardDisabled={isForwardDisabled}
              isNear
              isNightMode={isNightMode}
            />
          </NavigationWrapper>
        </HorizontalContainer>
        {displayVideoContainer && (
          <HorizontalContainer isNightMode={isNightMode}>
            <VideoButton
              feedback={videoFeedBack}
              isShortCopy={
                videoFeedBack.includes("STARTING_SOON_NO_REPLAY") &&
                get(race, "promo")
              }
              onClick={onVideoClick}
              isNightMode={isNightMode}
            />
            {get(race, "promo") && (
              <PromoButton
                url={
                  isExternal
                    ? promoPath
                    : buildPromoBadgeURL(
                        promoPath,
                        get(race, "raceNumber", "1")
                      )
                }
                isExternal={isExternal}
                onClick={() => onEventClick("PROMOS")}
                isOptedIn={isOptedIn}
                promoText={PromoTextXSell}
              />
            )}
          </HorizontalContainer>
        )}
      </Container>
    </React.Fragment>
  );
};

const RaceSelector = memo((props: Props) => {
  const { isHeaderSelector } = props;
  const { bannerProps } = useContext(FeedbackMessageContext);
  return isHeaderSelector
    ? renderHeaderSelector(props)
    : renderStandardSelector({ ...props, withFixedBanner: !!bannerProps });
});

RaceSelector.defaultProps = {
  isHeaderSelector: false,
  race: null,
  raceInfoData: [],
  onBackPageLink: "",
  onForwardPageLink: "",
  isBackDisabled: false,
  isForwardDisabled: false,
  hasPromo: false,
  promoText: {},
  MTPText: DefaultMTPText,
  showRace: false,
  prevPath: "",
  onEventClick: noop,
  trackSelectorUrl: "",
  onVideoClick: noop,
  videoFeedBack: "NOT_AVAILABLE_NO_REPLAY",
  bettingInterests: [],
  loadingRunners: true,
  raceAnalystVerdictExpanded: false,
  setRaceAnalystVerdictExpanded: noop,
  isWagerable: true,
  isSRW: false,
  freePickSource: "handicapping",
  racePromoPathParsed: "",
  withFixedBanner: false,
  hasFAQ: false
};

export default RaceSelector;
